import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';

const NAV_OPTIONS = [
    {id: 1, label: "home", to: "/"},
    {id: 2, label: "play_now", to: "/play"},
    {id: 3, label: "about_us", to: "/about-us"},
    {id: 4, label: "contact_us", to: "/contact-us"}
];

const NavigationView = () => {
    const theme = useTheme();

    const [openMobileMenu, setOpenMobileMenu] = useState(false);
    const [activePage, setActivePage] = useState('');

    useEffect(() => {
        const location = window.location.href;
        const lastIndexOfSlash = location.lastIndexOf("/");

        if(location.length - lastIndexOfSlash > 1){
            console.log(location.substring(lastIndexOfSlash));

            let index = -1;

            for(let i = 0; i < NAV_OPTIONS.length; i++){
                if(NAV_OPTIONS[i].to === location.substring(lastIndexOfSlash)){
                    index = i;
                    break;
                }
            }

            if(index !== -1){
                setActivePage(NAV_OPTIONS[index].to);
            }
            else{
                setActivePage('');
            }
        }
        else if(location.length - lastIndexOfSlash === 1){
            setActivePage('/');
        }
        else{
            setActivePage('');
        }
    }, []);

    const navigate = useNavigate();

    const handleCloseNavMenu = useCallback((path) => {
        setOpenMobileMenu(false);
        navigate(path);
    }, []);

    const toggleDrawer = (isOpen) => {
        setOpenMobileMenu(isOpen);
    };

    return (
        <>
            {/* Navigation Bar */}
            <AppBar position="fixed" id="header">
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        <LogoName />

                        {/* Navigation Options - PC View */}
                        <Box 
                            sx={{ 
                                flexGrow: 1, 
                                display: { xs: 'none', md: 'flex' },
                                justifyContent: 'center'
                            }}
                        >
                            {
                                NAV_OPTIONS.map((page, index) => (
                                    <NavigationOption 
                                        key={index}
                                        label={page.label} 
                                        redirection={page.to} 
                                        handleCloseNavMenu={handleCloseNavMenu}
                                        activePage={activePage}
                                    />
                                ))
                            }
                        </Box>

                        {/* Navigation Expand Icon - Mobile View */}
                        <Box 
                            sx={{ 
                                flexGrow: 1, 
                                display: { xs: 'flex', md: 'none' },
                                justifyContent: 'end'
                            }}
                        >
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={() => toggleDrawer(true)}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Navigation Menu */}
            <Drawer 
                open={openMobileMenu} 
                onClose={() => toggleDrawer(false)}
                anchor="right"
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: theme.palette.primary.main
                        }
                    }
                }}
                ModalProps={{
                    keepMounted: true
                }}
            >
                <Box 
                    sx={{ 
                        width: {xs: 200, md: 300}
                    }} 
                    role="presentation" 
                    pt={3}
                >
                    <List>
                        <Stack
                            direction='column'
                            justifyContent='start'
                            alignItems='center'
                            spacing={5}
                        >
                            {
                                NAV_OPTIONS.map((page, index) => (
                                    <NavigationOption 
                                        key={index}
                                        label={page.label} 
                                        redirection={page.to} 
                                        handleCloseNavMenu={handleCloseNavMenu}
                                        activePage={activePage}
                                    />
                                ))
                            }
                        </Stack>
                    </List>
                </Box>
            </Drawer>
        </>
    );
}

export default NavigationView;

const LogoName = () => {
    const {t} = useTranslation();

    return <Link 
        to="/"
        style={{
            textDecoration: 'none'
        }}
    >
        <Stack
            direction='row'
            justifyContent='start'
            alignItems='center'
            sx={{ 
                mr: 1,
                my: 2
            }}
        >
            <Box
                sx={{
                    width: '50px'
                }}
            >
                <img 
                    src='/images/logos/EdOnline Icon - White.png' 
                    alt='Logo'
                    width='100%'
                />
            </Box>

            <Typography
                variant="h6"
                noWrap
                sx={{
                    mr: 2,
                    fontWeight: 600,
                    textDecoration: 'none',
                    color: 'white'
                }}
                data-aos="fade-down"
                data-aos-easing="ease-out-back"
            >
                {t(`navigation.name`)}
            </Typography>
        </Stack>
    </Link>
}

const NavigationOption = ({label, redirection, handleCloseNavMenu, activePage}) => {
    const {t} = useTranslation();

    return  <Button
        onClick={() => handleCloseNavMenu(redirection)}
        sx={{ 
            my: 2, 
            mx: 2,
            color: 'white', 
            display: 'block',
            textTransform: 'none',
            fontWeight: activePage === redirection ? 700 : 400,
            fontSize: activePage === redirection ? 16 : 14,
            '&: hover': {
                transform: 'scale(0.9)'
            }
        }}
        data-aos="zoom-in"
    >
        {t(`navigation.${label}`)}
    </Button>
}